import { useEffect, useState, Component } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@/App.css';
import axios from 'axios';
import Dashboard from '@/components/Dashboard';
import AuthPage from '@/components/AuthPage';
import LandingPage from '@/components/LandingPage';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { AuthProvider, useAuth, setupAxiosInterceptors } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { initNotifications } from '@/services/notificationService';
import { addResourceHints } from '@/utils/cdn';

// Initialize CDN resource hints early
if (typeof window !== 'undefined') {
  addResourceHints();
}

// React Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // Data is fresh for 5 minutes
      gcTime: 30 * 60 * 1000,        // Cache for 30 minutes (formerly cacheTime)
      retry: 2,                       // Retry failed requests twice
      refetchOnWindowFocus: true,     // Refresh when user returns to tab
      refetchOnReconnect: true,       // Refresh when network reconnects
    },
  },
});

// Error Boundary to catch JavaScript errors and prevent white screen
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-8 max-w-md w-full text-center border border-slate-700">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-slate-400 mb-6">
              The app encountered an error. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Refresh Page
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-4 p-3 bg-slate-900 rounded-lg text-left">
                <p className="text-red-400 text-sm font-mono break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Setup axios interceptors for auth
setupAxiosInterceptors();

// Initialize service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    initNotifications().then((success) => {
      if (success) {
        console.log('PWA: Service Worker initialized');
      }
    });
  });
}

function AppContent() {
  const { isAuthenticated, loading: authLoading, user, isDemo, enterDemoMode, exitDemoMode } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showLanding, setShowLanding] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  // Demo data for non-authenticated demo mode
  const DEMO_STOCKS = [
    { symbol: 'AAPL', name: 'Apple Inc.', price: 178.50, change: 2.34, changePercent: 1.33 },
    { symbol: 'NVDA', name: 'NVIDIA Corp.', price: 875.25, change: 15.75, changePercent: 1.83 },
    { symbol: 'MSFT', name: 'Microsoft Corp.', price: 378.90, change: -1.20, changePercent: -0.32 },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', price: 141.80, change: 3.45, changePercent: 2.49 },
    { symbol: 'META', name: 'Meta Platforms', price: 485.60, change: 8.90, changePercent: 1.87 },
  ];

  const DEMO_ALERTS = [
    { id: 1, type: 'ENTRY_ZONE', symbol: 'AAPL', message: 'Approaching key support at $175', priority: 'high', created_at: new Date().toISOString() },
    { id: 2, type: 'TRADE_SCORE', symbol: 'NVDA', message: 'A+ setup detected - RSI divergence', priority: 'high', created_at: new Date().toISOString() },
    { id: 3, type: 'LIQUIDITY_SWEEP', symbol: 'MSFT', message: 'Weekly low swept - potential reversal', priority: 'medium', created_at: new Date().toISOString() },
  ];

  const fetchStocks = async () => {
    try {
      const response = await axios.get(`${API}/watchlist/stocks`);
      setStocks(response.data || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching stocks:', error);
      setStocks([]);
    }
  };

  const fetchAlerts = async () => {
    try {
      // Use the new /alerts/best endpoint for deduplicated, quality-filtered alerts
      const response = await axios.get(`${API}/alerts/best`);
      // The new endpoint returns { alerts: [...], total_raw_alerts, best_alerts_count }
      setAlerts(response.data?.alerts || response.data || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      // Fallback to old endpoint if new one fails
      try {
        const fallback = await axios.get(`${API}/alerts/active`);
        setAlerts(fallback.data || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
        setAlerts([]);
      }
    }
  };

  useEffect(() => {
    // If user is authenticated (real or demo), hide landing
    if (isAuthenticated) {
      setShowLanding(false);
      setShowAuth(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // If demo mode, use demo data
    if (isDemo) {
      setStocks(DEMO_STOCKS);
      setAlerts(DEMO_ALERTS);
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStocks(), fetchAlerts()]);
      setLoading(false);
    };

    loadData();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStocks();
      fetchAlerts();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, isDemo]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading TradeRelax...</span>
        </div>
      </div>
    );
  }

  // Show auth page if requested
  if (showAuth && !isAuthenticated) {
    return <AuthPage onBack={() => setShowAuth(false)} />;
  }

  // Show landing page if not authenticated and landing is shown
  if (!isAuthenticated && showLanding) {
    return (
      <LandingPage 
        onGetStarted={() => setShowAuth(true)}
        onTryDemo={() => {
          enterDemoMode();
          setShowLanding(false);
        }}
      />
    );
  }

  // Show auth page if not authenticated
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  // Show dashboard if authenticated
  return (
    <Dashboard 
      stocks={stocks}
      alerts={alerts}
      loading={loading}
      lastUpdate={lastUpdate}
      isDemo={isDemo}
      onExitDemo={exitDemoMode}
      onRefresh={async () => {
        if (isDemo) return;
        setLoading(true);
        await Promise.all([fetchStocks(), fetchAlerts()]);
        setLoading(false);
      }}
    />
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <div className="App">
            <AppContent />
            <PWAInstallPrompt />
            <Toaster position="top-right" />
          </div>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
